import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Lock, User, Info, Building2, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AfricaNendaLogo from '../components/AfricaNendaLogo';
import SupportContact from '../components/SupportContact';
import { loginPrefill, firstEmptyLoginField } from '../utils/onboarding';

// MULTI_TENANT mode is signalled by a build-time env var.
// In SINGLE_TENANT deployments this is unset/false — the Tenant ID field
// is hidden entirely and the login flow is identical to the original.
const IS_MULTI_TENANT = import.meta.env.VITE_DEPLOYMENT_MODE === 'MULTI_TENANT';
// Only the Operator Console build registers /platform/login; in the participant
// build that route falls through to this page, so the link would loop.
const HAS_CONSOLE = import.meta.env.VITE_ENABLE_PLATFORM_ADMIN === 'true';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Arriving from activation or the "account active" email, the tenant id (and,
  // from activation, the username) are already known.
  const [prefill] = useState(() => loginPrefill(location.state, location.search));
  const focusField = firstEmptyLoginField(prefill, IS_MULTI_TENANT);
  const [formData, setFormData] = useState({
    username: prefill.username,
    password: '',
    tenantId: prefill.tenantId,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.username.trim()) {
      setError('Username or email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    // In MULTI_TENANT mode, Tenant ID is required
    if (IS_MULTI_TENANT && !formData.tenantId.trim()) {
      setError('Tenant ID is required');
      return;
    }

    try {
      setLoading(true);

      // Pass tenantId only in MULTI_TENANT mode (undefined otherwise — backend ignores it)
      const result = await login(
        formData.username,
        formData.password,
        IS_MULTI_TENANT ? formData.tenantId : undefined,
      );

      if (result?.user?.mustChangePassword) {
        navigate('/change-password', {
          state: {
            forced: true,
            message: 'Your password was reset. Please set a new password to continue.',
          },
        });
        return;
      }
      // Normal login — navigation handled by AuthContext.login()

    } catch (err) {
      console.error('Login failed:', err);
      setError(err.response?.data?.error || err.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">

        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <AfricaNendaLogo className="h-11" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            IIPS Participant Portal
          </h1>
          <p className="text-gray-500 text-sm">AfricaNenda IIPS Sandbox</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-xl p-8">

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Info className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-900">Success</p>
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-900">Login Failed</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Tenant ID — MULTI_TENANT mode only */}
            {IS_MULTI_TENANT && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="tenantId"
                    value={formData.tenantId}
                    onChange={handleChange}
                    className="input pl-10"
                    placeholder="e.g. acme-rw"
                    autoFocus={focusField === 'tenantId'}
                    autoComplete="organization"
                    spellCheck={false}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  The tenant ID your institution chose when it applied.
                </p>
              </div>
            )}

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username or email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="Your username or email address"
                  autoFocus={focusField === 'username'}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input pl-10 pr-10"
                  placeholder="Enter your password"
                  autoFocus={focusField === 'password'}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword
                    ? <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center space-x-2 py-3"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Register CTA — self-service onboarding (MULTI_TENANT only) */}
          {IS_MULTI_TENANT && (
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-600 mb-2">New to the sandbox?</p>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-lg
                           border border-primary-600 text-primary-600 font-medium hover:bg-primary-50 transition-colors"
              >
                <UserPlus className="w-4 h-4" /> Apply to join
              </Link>
            </div>
          )}

          {/* Help text */}
          <div className="mt-6 text-center space-y-1">
            <p className="text-sm text-gray-600">Need help accessing your account?</p>
            <SupportContact lead="Ask an admin at your institution to reset your password, or email AfricaNenda at"
              subject="Help signing in to the AfricaNenda IIPS Sandbox" />
            {IS_MULTI_TENANT && HAS_CONSOLE && (
              <p className="text-xs text-gray-400 pt-2 border-t border-gray-100 mt-3">
                AfricaNenda operator?{' '}
                <Link to="/platform/login" className="text-primary-600 hover:text-primary-700 font-medium">
                  Operator Console →
                </Link>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">AfricaNenda Foundation</p>
          <p className="text-xs text-gray-500 mt-1">IIPS Sandbox v0.1.0</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
